import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';

import scrollIntoViewIfNeeded, {
	type StandardBehaviorOptions as ScrollOptions,
} from 'scroll-into-view-if-needed';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--jira-thor/wiv-beta-changeboarding';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { Text } from '@atlaskit/primitives/compiled';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';

import { type MessageTemplateContext } from '../../../index';

// Anchor for step 2. When the Assignee context field is present it owns a dedicated
// SpotlightTarget; otherwise fall back to the topmost field in the wide-view details
// sidebar. The fallback is scoped to the sidebar tab content so it can never resolve to
// the identical field-mapper testid rendered in the left/main column.
const FIELDS_ASSIGNEE_TARGET = '#wiv-beta-changeboarding-fields-target';

const DETAILS_SIDEBAR_SCOPE =
	'[data-testid^="work-item.ui.components.tab"][data-testid$="-content"]';

const FIELDS_TOPMOST_TARGET = `${DETAILS_SIDEBAR_SCOPE} [data-testid="work-item-dynamic-field-mapper.pinned-fields-section"] > :first-child`;

const TARGETS = {
	summary: '#wiv-beta-changeboarding-summary-target',
	fields: FIELDS_ASSIGNEE_TARGET,
	attachments: '#wiv-beta-changeboarding-attachments-target',
} as const;

const messages = defineMessages({
	next: {
		id: 'post-office.jira-wiv-beta-changeboarding.next',
		defaultMessage: 'Next',
		description: 'Primary action label that advances the WIV beta changeboarding spotlight.',
	},
	back: {
		id: 'post-office.jira-wiv-beta-changeboarding.back',
		defaultMessage: 'Back',
		description:
			'Secondary action label that returns to the previous WIV beta changeboarding step.',
	},
	gotIt: {
		id: 'post-office.jira-wiv-beta-changeboarding.got-it',
		defaultMessage: 'OK',
		description: 'Primary action label that completes the WIV beta changeboarding spotlight.',
	},
	whyHeading: {
		id: 'post-office.jira-wiv-beta-changeboarding.why-heading',
		defaultMessage: 'A clearer work item',
		description: 'Headline for the first WIV beta changeboarding spotlight step.',
	},
	whyBody: {
		id: 'post-office.jira-wiv-beta-changeboarding.why-body',
		defaultMessage:
			"We've refreshed the work item to highlight what matters most. Share your feedback anytime from <bold>More actions (•••)</bold>, then <bold>Give feedback</bold>.",
		description: 'Body copy for the first WIV beta changeboarding spotlight step.',
	},
	fieldsHeading: {
		id: 'post-office.jira-wiv-beta-changeboarding.fields-heading',
		defaultMessage: 'Simpler fields',
		description: 'Headline for the second WIV beta changeboarding spotlight step.',
	},
	fieldsBody: {
		id: 'post-office.jira-wiv-beta-changeboarding.fields-body',
		defaultMessage:
			'Field names and values now appear together in a cleaner inline layout. Everything is still here, just easier to scan and edit.',
		description: 'Body copy for the second WIV beta changeboarding spotlight step.',
	},
	attachmentsHeading: {
		id: 'post-office.jira-wiv-beta-changeboarding.attachments-heading',
		defaultMessage: 'All your files in one place',
		description: 'Headline for the final WIV beta changeboarding spotlight step.',
	},
	attachmentsBody: {
		id: 'post-office.jira-wiv-beta-changeboarding.attachments-body',
		defaultMessage:
			"Attachments, Confluence pages, Loom recordings, and links now live under Attachments. Everything is in one place, so it's easier to find what you need.",
		description: 'Body copy for the final WIV beta changeboarding spotlight step.',
	},
});

type StepIndex = 0 | 1 | 2;

type ChangeboardingAction = 'dismissIcon' | 'gotIt';

type Step = {
	target: (typeof TARGETS)[keyof typeof TARGETS];
	placement: 'bottom-end' | 'bottom-start';
	stepCount: '1/3' | '2/3' | '3/3';
	heading: ReactNode;
	body: ReactNode;
};

const NEXT_STEP_INDEX: Record<StepIndex, StepIndex> = {
	0: 1,
	1: 2,
	2: 2,
};

const PREVIOUS_STEP_INDEX: Record<StepIndex, StepIndex> = {
	0: 0,
	1: 0,
	2: 1,
};

const STEP_NUMBER_BY_INDEX: Record<StepIndex, 1 | 2 | 3> = {
	0: 1,
	1: 2,
	2: 3,
};

const STEP_TARGET_SCROLL_OPTIONS: ScrollOptions = {
	scrollMode: 'if-needed',
	block: 'center',
};

type RenderProps = MessageTemplateContext & PlacementRenderProps;

const useTargetRenderer = (target: string) =>
	useMountToHtmlElement({
		selectors: target,
		as: 'inline',
		position: 'inside',
		observeRoot: typeof globalThis.document !== 'undefined' ? globalThis.document.body : undefined,
	});

// Resolve the step 2 anchor by preference (Assignee target, else topmost sidebar field)
// rather than a comma-separated selector list, because `querySelector` returns the first
// match in DOM order, not the first selector listed. Re-checks as the details panel mounts.
const useFieldsTarget = (isActive: boolean): string => {
	const [fieldsTarget, setFieldsTarget] = useState<string>(FIELDS_ASSIGNEE_TARGET);

	useEffect(() => {
		if (!isActive || typeof globalThis.document === 'undefined') {
			return;
		}

		const doc = globalThis.document;
		const resolveTarget = () => {
			setFieldsTarget(
				doc.querySelector(FIELDS_ASSIGNEE_TARGET) ? FIELDS_ASSIGNEE_TARGET : FIELDS_TOPMOST_TARGET,
			);
		};

		resolveTarget();

		const observer = new MutationObserver(resolveTarget);
		observer.observe(doc.body, { childList: true, subtree: true });

		return () => observer.disconnect();
	}, [isActive]);

	return fieldsTarget;
};

function DeliveredTour({ renderTour, onDismiss }: PlacementRenderProps): ReactNode {
	const { messageDismissed } = useMessageEvent();
	const { onStepChanged, onAcknowledged, onClosed } = usePostOfficeUserInteractions();
	const handleInteraction = (action: 'next' | 'back', step: number) => {
		if (action === 'next') {
			void onStepChanged({
				interactionIdentifier: 'next',
				direction: 'next',
				fromStep: step,
				toStep: step + 1,
			});
		} else {
			void onStepChanged({
				interactionIdentifier: 'back',
				direction: 'previous',
				fromStep: step,
				toStep: step - 1,
			});
		}
	};
	const handleFinish = (outcome: 'completed' | 'dismissed') => {
		if (outcome === 'completed') {
			void onAcknowledged({ interactionIdentifier: 'gotIt' });
		} else {
			void onClosed({ interactionIdentifier: 'dismissIcon' });
		}
		void messageDismissed();
		onDismiss?.();
	};
	return renderTour?.({ onInteraction: handleInteraction, onFinish: handleFinish });
}

function LegacyTour({ onDismiss }: RenderProps): ReactNode {
	const { messageDismissed } = useMessageEvent();
	const { onStepChanged, onAcknowledged, onClosed } = usePostOfficeUserInteractions();
	const [activeStepIndex, setActiveStepIndex] = useState<StepIndex>(0);
	const [isVisible, setIsVisible] = useState(true);
	const fieldsTarget = useFieldsTarget(activeStepIndex === 1);
	const targetByStepIndex: Record<StepIndex, string> = {
		0: TARGETS.summary,
		1: fieldsTarget,
		2: TARGETS.attachments,
	};
	const activeTarget = targetByStepIndex[activeStepIndex];
	// Mount only the active step inside its own target so the spotlight always
	// anchors to that step's element (mirrors jira-spotlight.tsx). Mounting every
	// step at once left inactive anchors positioning the dialog at a fixed spot.
	const renderActiveTarget = useTargetRenderer(activeTarget);

	useEffect(() => {
		if (typeof globalThis.document === 'undefined') {
			return;
		}

		const targetElement = globalThis.document.querySelector(activeTarget);

		if (targetElement) {
			scrollIntoViewIfNeeded(targetElement, STEP_TARGET_SCROLL_OPTIONS);
		}
	}, [activeTarget]);

	const steps: readonly [Step, Step, Step] = [
		{
			target: TARGETS.summary,
			placement: 'bottom-end',
			stepCount: '1/3',
			heading: <FormattedMessage {...messages.whyHeading} />,
			body: (
				<FormattedMessage
					{...messages.whyBody}
					values={{
						bold: (chunks: ReactNode) => <Text as="strong">{chunks}</Text>,
					}}
				/>
			),
		},
		{
			target: TARGETS.fields,
			placement: 'bottom-start',
			stepCount: '2/3',
			heading: <FormattedMessage {...messages.fieldsHeading} />,
			body: <FormattedMessage {...messages.fieldsBody} />,
		},
		{
			target: TARGETS.attachments,
			placement: 'bottom-end',
			stepCount: '3/3',
			heading: <FormattedMessage {...messages.attachmentsHeading} />,
			body: <FormattedMessage {...messages.attachmentsBody} />,
		},
	];

	const dismiss = useCallback(
		(action: Extract<ChangeboardingAction, 'dismissIcon' | 'gotIt'>) => {
			if (action === 'gotIt') {
				void onAcknowledged({ interactionIdentifier: 'gotIt' });
			} else {
				void onClosed({ interactionIdentifier: 'dismissIcon' });
			}
			void messageDismissed();
			setIsVisible(false);
			onDismiss?.();
		},
		[messageDismissed, onAcknowledged, onClosed, onDismiss],
	);

	const next = useCallback(() => {
		const fromStep = STEP_NUMBER_BY_INDEX[activeStepIndex];
		void onStepChanged({
			interactionIdentifier: 'next',
			direction: 'next',
			fromStep,
			toStep: fromStep + 1,
		});
		setActiveStepIndex((currentStepIndex) => NEXT_STEP_INDEX[currentStepIndex]);
	}, [activeStepIndex, onStepChanged]);

	const back = useCallback(() => {
		const fromStep = STEP_NUMBER_BY_INDEX[activeStepIndex];
		void onStepChanged({
			interactionIdentifier: 'back',
			direction: 'previous',
			fromStep,
			toStep: fromStep - 1,
		});
		setActiveStepIndex((currentStepIndex) => PREVIOUS_STEP_INDEX[currentStepIndex]);
	}, [activeStepIndex, onStepChanged]);

	const renderStep = (stepIndex: StepIndex) => {
		const step = steps[stepIndex];

		return (
			<PopoverProvider>
				<PopoverTarget>
					<div />
				</PopoverTarget>
				<PopoverContent
					testId={`jira-wiv-beta-changeboarding.step-${STEP_NUMBER_BY_INDEX[stepIndex]}`}
					dismiss={() => dismiss('dismissIcon')}
					placement={step.placement}
					isVisible={isVisible}
					shouldDismissOnClickOutside={true}
				>
					<SpotlightCard>
						<SpotlightHeader>
							<SpotlightHeadline>{step.heading}</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl onClick={() => dismiss('dismissIcon')} />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightBody>
							<Text>{step.body}</Text>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightStepCount>{step.stepCount}</SpotlightStepCount>
							<SpotlightActions>
								{stepIndex === 0 ? (
									<SpotlightPrimaryAction
										onClick={next}
										testId="work-item-layout.beta-changeboarding.next"
									>
										<FormattedMessage {...messages.next} />
									</SpotlightPrimaryAction>
								) : null}
								{stepIndex === 1 ? (
									<>
										<SpotlightSecondaryAction
											onClick={back}
											testId="work-item-layout.beta-changeboarding.back"
										>
											<FormattedMessage {...messages.back} />
										</SpotlightSecondaryAction>
										<SpotlightPrimaryAction
											onClick={next}
											testId="work-item-layout.beta-changeboarding.next"
										>
											<FormattedMessage {...messages.next} />
										</SpotlightPrimaryAction>
									</>
								) : null}
								{stepIndex === 2 ? (
									<>
										<SpotlightSecondaryAction
											onClick={back}
											testId="work-item-layout.beta-changeboarding.back"
										>
											<FormattedMessage {...messages.back} />
										</SpotlightSecondaryAction>
										<SpotlightPrimaryAction
											onClick={() => dismiss('gotIt')}
											testId="work-item-layout.beta-changeboarding.got-it"
										>
											<FormattedMessage {...messages.gotIt} />
										</SpotlightPrimaryAction>
									</>
								) : null}
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</PopoverProvider>
		);
	};

	if (!isVisible) {
		return null;
	}

	return <>{renderActiveTarget(renderStep(activeStepIndex))}</>;
}

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<RenderProps>(),
		Component: (props: RenderProps) => {
			if (props.renderTour) {
				return <DeliveredTour {...props} />;
			}
			// Host-rendered content: Post Office decides when to render, the host renders the UI.
			if (FeatureGates.checkGate('wiv_thor_ga_change-boarding') && props.renderContent) {
				return <DeliveredTour {...props} renderTour={props.renderContent} />;
			}
			return <LegacyTour {...props} />;
		},
	},
});
